'use client';

import { useFestivalContext } from '@/contexts/FestivalContext';
import { useUserStore } from '@/store/useUserStore';
import { getApiAuthHeaders } from '@/utils/api-helpers';
import { handleFormSubmitApiError } from '@/utils/form-submit-error';
import { cn } from '@/utils/tailwind-merge';
import { filmmakerSchema } from '@/utils/validation-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import AccreditationSuccess from '../blocks/AccreditationSuccess';
import Button from '../ui/Button';
import Input from '../ui/Input';

type FormData = z.infer<typeof filmmakerSchema>;
const URL = process.env.NEXT_PUBLIC_API_URL || 'https://image-nation.org/api-service';

export default function FilmmakerForm({ className }: { className?: string }) {
    const t = useTranslations();
    const locale = useLocale();
    const { user, session } = useUserStore();
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [isSuccessful, setIsSuccessful] = useState<boolean>(false);
    const { festival } = useFestivalContext();

    const { handleSubmit, register, reset } = useForm<FormData>({
        resolver: zodResolver(filmmakerSchema),
    });

    const onSubmit = async (formData: FormData) => {
        setSubmitting(true);

        if (!user) {
            setSubmitting(false);
            return;
        }
        try {
            const response = await fetch(`${URL}/form/accreditation/filmmaker`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept-Language': locale,
                    ...getApiAuthHeaders(session?.access_token),
                },
                body: JSON.stringify({
                    user_id: user.id,
                    formData: formData,
                    festival_id: festival?.id,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to submit the form');
            }

            reset();
            setIsSuccessful(true);
        } catch (error) {
            handleFormSubmitApiError(error, t);
        } finally {
            setSubmitting(false);
        }
    };

    return isSuccessful ? (
        <div
            className={cn(
                'w-full flex flex-col gap-7 justify-center items-center relative',
                className,
            )}
        >
            <AccreditationSuccess className="mt-20 w-full" />
        </div>
    ) : (
        <form
            className={cn(
                'flex flex-col gap-7 justify-center items-center relative text-white',
                className,
            )}
            onSubmit={handleSubmit(onSubmit)}
        >
            <div className="grid grid-cols-1 xl:grid-cols-3 lg:gap-5 gap-3 w-full">
                <Input
                    {...register('filmTitle')}
                    type="text"
                    name="filmTitle"
                    placeholder={t('FilmmakerForm.filmTitle')}
                    autoComplete="false"
                    color="white"
                    required
                />
                <div>
                    <Input
                        {...register('filmRole')}
                        type="text"
                        name="filmRole"
                        placeholder={t('FilmmakerForm.filmRole')}
                        autoComplete="false"
                        color="white"
                        required
                    />
                    <p className="mt-2.5 font-helvetica-neue font-light text-lg text-white/60">
                        {t('AccreditationPage.roleInFilm')}
                    </p>
                </div>
                <Input
                    {...register('instagram')}
                    type="text"
                    name="instagram"
                    placeholder={t('FilmmakerForm.instagram')}
                    autoComplete="false"
                    color="white"
                />
                <Input
                    {...register('facebook')}
                    type="text"
                    name="facebook"
                    placeholder={t('FilmmakerForm.facebook')}
                    autoComplete="false"
                    color="white"
                />
                <Input
                    {...register('tiktok')}
                    type="text"
                    name="tiktok"
                    placeholder={t('FilmmakerForm.tiktok')}
                    autoComplete="false"
                    color="white"
                />
                <Input
                    {...register('youtube')}
                    type="text"
                    name="youtube"
                    placeholder={t('FilmmakerForm.youtube')}
                    autoComplete="false"
                    color="white"
                />
                <Input
                    {...register('vimeo')}
                    type="text"
                    name="vimeo"
                    placeholder={t('FilmmakerForm.vimeo')}
                    autoComplete="false"
                    color="white"
                />
                <Input
                    {...register('linkedin')}
                    type="text"
                    name="linkedin"
                    placeholder={t('FilmmakerForm.linkedin')}
                    autoComplete="false"
                    color="white"
                />
                <Input
                    {...register('behance')}
                    type="text"
                    name="behance"
                    placeholder={t('FilmmakerForm.behance')}
                    autoComplete="false"
                    color="white"
                />
                <div>
                    <Input
                        {...register('website')}
                        type="text"
                        name="website"
                        placeholder={t('FilmmakerForm.website')}
                        autoComplete="false"
                        color="white"
                    />
                    <p className="mt-2.5 font-helvetica-neue font-light text-lg text-white/60">
                        {t('AccreditationPage.websiteSub')}
                    </p>
                </div>
            </div>
            <div className="flex flex-col justify-start w-full gap-10">
                {/* <p className="font-helvetica-neue font-light text-md text-left tracking-normal">
                    {t.rich('FilmmakerForm.contactUs', {
                        a: (chunks) => (
                            <a
                                href={''}
                                className="text-md font-light font-helvetica-neue text-accent"
                            >
                                {chunks}
                            </a>
                        ),
                    })}
                </p> */}
                <Button
                    className="w-44 bg-primary p-4"
                    textClasses="text-black uppercase"
                    type="submit"
                    title={t('Form.save')}
                    submitting={submitting}
                    disabled={submitting}
                />
            </div>
        </form>
    );
}
